import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Download } from 'lucide-react';

// Pastille(s) dans l'entête du projet : signale quel(s) SITE(S) est/sont connecté(s) et par
// quel(s) moteur(s). Un projet peut connecter plusieurs moteurs sur un même site (publication
// `site_internet` + ingestion `wordpress_source`) → on REGROUPE par host et on affiche une
// pastille verte par site (avec l'icône de chaque moteur). Absente si aucun site connecté.
//
// Avant : ne regardait QUE `site_internet` → un site connecté en lecture WP (ex. euroalgerie.org
// via `wordpress_source`) n'apparaissait pas. Désormais tout moteur lié à un site est couvert.

// Moteurs « liés à un site » (ceux qui portent un config.base_url) → libellé + icône.
const SITE_ENGINES = {
  site_internet:    { label: 'Publication', Icon: Globe },
  wordpress_source: { label: 'Source WP',   Icon: Download },
};

const hostOf = (url) => {
  if (!url) return null;
  try { return new URL(url).host; } catch { return url; } // base_url déjà un host
};

const ConnectedSiteBadge = ({ projectId, onClick }) => {
  const [sites, setSites] = useState([]); // [{ host, baseUrl, engines:[{code,label,Icon}] }]

  useEffect(() => {
    if (!projectId) { setSites([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await axios.get(`/api/connectors/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = (res.data && res.data.connectors) || [];
        // Regroupe les moteurs liés à un site par host de base_url.
        const byHost = new Map();
        for (const c of list) {
          if (c.status !== 'active') continue;
          const meta = SITE_ENGINES[c.connector_code];
          if (!meta) continue;
          const baseUrl = c.config && c.config.base_url;
          const host = hostOf(baseUrl);
          if (!host) continue;
          if (!byHost.has(host)) byHost.set(host, { host, baseUrl, engines: [] });
          // Évite les doublons si le même code apparaît 2×.
          const bucket = byHost.get(host);
          if (!bucket.engines.some(e => e.code === c.connector_code)) {
            bucket.engines.push({ code: c.connector_code, label: meta.label, Icon: meta.Icon });
          }
        }
        if (!cancelled) setSites(Array.from(byHost.values()));
      } catch {
        if (!cancelled) setSites([]);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (!sites.length) return null;

  return (
    <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
      {sites.map((site) => {
        const enginesLabel = site.engines.map(e => e.label).join(' + ');
        return (
          <div
            key={site.host}
            onClick={onClick}
            title={`Connecté · ${site.baseUrl} — ${enginesLabel}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '4px 10px', borderRadius: '6px',
              cursor: onClick ? 'pointer' : 'default',
              background: 'rgba(16, 185, 129, 0.10)',
              border: '1px solid rgba(16, 185, 129, 0.28)',
              fontSize: '11px', fontWeight: '600', color: '#10b981',
              transition: 'all 0.15s ease',
            }}
          >
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%', background: '#10b981',
              boxShadow: '0 0 6px rgba(16,185,129,0.5)',
            }} />
            {site.engines.map((e) => {
              const Ic = e.Icon;
              return <Ic key={e.code} size={12} strokeWidth={2.5} />;
            })}
            {site.host}
          </div>
        );
      })}
    </span>
  );
};

export default ConnectedSiteBadge;
